import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, FormField, FormGroup, Icon, Input, Modal } from "semantic-ui-react";

// Components
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { Table } from "react-lib/frameworks/Table";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import ModNewRegister, { NewRegisterFormData } from "react-lib/apps/MEH/ModNewRegister";
import ModPackageOrderDetail from "./ModPackageOrderDetail";
import ModRegisterServiceFromAppointment from "./ModRegisterServiceFromAppointment";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

// Serializers
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

// Types
import {
  SetProp,
  RunSequence,
  OnEvent,
  DEFAULT_LIST_LIMIT,
  PickedState,
  SelectedCustomerData,
  VisitListData,
  AppointmentListData,
  BTN_ACTS,
} from "./types/MeBillingType";
import { useIntl } from "react-intl";

type CardRegisterServiceProps = {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: OnEvent;

  // callback
  onNavigateToSaveService?: () => void;
  onOpenEnAppointmentSuccess?: () => void;

  // data
  selectedCustomer: SelectedCustomerData | undefined;
  visitList: VisitListData | undefined;
  appointmentList: AppointmentListData | undefined;
  packageDetail: Record<string, any>[];
  // options
  doctorList: DoctorSerializer[];
  masterOptions?: Record<string, any[]>;
  //
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "selectedDivision" | "searchedItemListWithKey">;

const CardRegisterService = (props: CardRegisterServiceProps) => {
  // todo --- Appointment List / Search (สำหรับเปิด Encounter ผ่าน Booking)
  const intl = useIntl();
  const [searchText, setSearchText] = useState<string>("");
  const [openNewRegisterMod, setOpenNewRegisterMod] = useState<boolean>(false);
  const [modRegisterPackage, setModRegisterPackage] = useState<PatientAppointmentSerializer | null>(
    null,
  );

  useEffect(() => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_APPOINTMENT_LIST",
      filter: {
        patient_id: props.selectedCustomer?.patientDetail?.id,
      },
      activePage: 1,
    });
  }, [props.selectedCustomer?.patientDetail?.id]);

  const handleChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);

  const handleSearch = useCallback(() => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_APPOINTMENT_LIST",
      filter: {
        patient_id: props.selectedCustomer?.patientDetail?.id,
        ...(searchText && { id__in: searchText }),
      },
      activePage: 1,
    });
  }, [searchText, props.runSequence, props.selectedCustomer]);

  const handleOpenNewRegister = useCallback(() => {
    setOpenNewRegisterMod(true);
  }, []);

  const handleCloseNewRegister = useCallback(() => {
    setOpenNewRegisterMod(false);
  }, []);

  const handleSaveRegister = useCallback(
    (data: NewRegisterFormData) => {
      // *** NOTE: Create Step: ให้ action เป็น "OPD_NEW"
      props.runSequence({
        sequence: "MeBilling",
        action: "CREATE_VISIT",
        visitData: {
          doctor: data.provider_id,
          patient: props.selectedCustomer?.patientDetail?.id || 0,
          type: "OPD",
          action: "OPD_NEW",
        },
        onSuccess: () => {
          handleCloseNewRegister();
          props.onNavigateToSaveService?.();
        },
      });
    },
    [props.runSequence, props.selectedCustomer, props.onNavigateToSaveService],
  );

  const handleRegister = useCallback(
    (
      item: PatientAppointmentSerializer,
      appointment: { isConfirm?: boolean; providerName?: string } = {
        isConfirm: false,
        providerName: "",
      },
    ) => {
      if (!item.id || !props.selectedCustomer?.patientDetail?.id) {
        console.warn("[Open Visit From Appointment] Appointment ID Not Found!!");

        return;
      }

      if (item.appointment_type === "PACKAGE" && !appointment.isConfirm) {
        setModRegisterPackage(item);

        return;
      }

      props.runSequence({
        sequence: "MeBilling",
        action: "CREATE_VISIT_APPOINTMENT",
        appointmentId: item.id,
        patient: props.selectedCustomer?.patientDetail?.id,
        providerName: appointment.providerName,
        onSuccess: () => {
          props.onOpenEnAppointmentSuccess?.();
          props.onNavigateToSaveService?.();
        },
      });
    },
    [props.runSequence, props.selectedCustomer],
  );

  const handlePageChange = useCallback(
    (activePage: number) => {
      props.runSequence({
        sequence: "MeBilling",
        action: "GET_APPOINTMENT_LIST",
        filter: {
          patient_id: props.selectedCustomer?.patientDetail?.id,
        },
        activePage,
      });
    },
    [props.selectedCustomer],
  );

  const handleOpenModPackageDetail = useCallback(
    (packageOrders: Record<string, any>[], index: number) => {
      props.runSequence({
        sequence: "MeBilling",
        action: "GET_PACKAGE_DETAIL",
        packageIds: packageOrders.map((pk) => pk.product),
        index,
      });
    },
    [],
  );

  const formattedRegisterList = useMemo(() => {
    const dataList = props.appointmentList?.items || [];

    return dataList.map((item, index) => ({
      ...item,
      id: <div style={{ display: "flex", justifyContent: "center" }}>{item.id}</div>,
      datetime: (
        <span>
          {item.date} [{item.start_time}]
        </span>
      ),
      package_detail: (
        <>
          {item.package_orders?.map((pk: any) => (
            <div key={pk.id} style={{ display: "grid", gridTemplateColumns: "6.5rem auto" }}>
              <div
                style={{
                  color: pk.package_type_name === "Package" ? "#5D8439" : "#FE9500",
                }}
              >
                {pk.package_type_name}
              </div>
              <div>{pk.product_name}</div>
            </div>
          ))}
          {!!item.package_orders?.length && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="mini"
                style={{
                  backgroundColor: "rgba(255, 222, 175, 1)",
                  padding: "0.4rem 0.25rem",
                }}
                loading={!!props.buttonLoadCheck?.[`${BTN_ACTS.GET_PACKAGE_DETAIL}_${index}`]}
                onClick={() => {
                  handleOpenModPackageDetail(item.package_orders, index);
                }}
              >
                {intl.formatMessage({ id: "REGISTER_SERVICE_รายละเอียด Package" })}
              </Button>
            </div>
          )}
        </>
      ),
      register: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ButtonLoadCheck
            key={item.id}
            setProp={props.setProp}
            paramKey={`${BTN_ACTS.CREATE_VISIT_APPOINTMENT}_${item.id}`}
            buttonLoadCheck={
              props.buttonLoadCheck?.[`${BTN_ACTS.CREATE_VISIT_APPOINTMENT}_${item.id}`]
            }
            //
            size="small"
            style={{
              backgroundColor: "var(--primary-theme-color)",
              color: "white",
              paddingLeft: "0.35rem",
              paddingRight: "0.35rem",
            }}
            onClick={() => handleRegister(item)}
            title={intl.formatMessage({ id: "REGISTER_SERVICE_ลงทะเบียนรับบริการ" })}
          />
        </div>
      ),
    }));
  }, [props.appointmentList, handleRegister, props.setProp, props.buttonLoadCheck, props.languageUX]);

  return (
    <div style={{ padding: "1rem" }}>
      {/* --- New Register Mod --- */}
      <ModNewRegister
        open={openNewRegisterMod}
        // callbacks
        onClose={handleCloseNewRegister}
        onSave={handleSaveRegister}
        // options
        doctorList={props.doctorList}
        // common
        selectedDivision={props.selectedDivision}
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />

      <Form>
        <FormGroup style={{ marginBottom: 0 }}>
          <FormField>
            <div style={{ width: "100%" }}>
              <Button
                fluid
                style={{
                  backgroundColor: "#87b5ff",
                  color: "white",
                }}
                onClick={handleOpenNewRegister}
              >
                <Icon name="plus" />
                {intl.formatMessage({ id: "REGISTER_SERVICE_ลงทะเบียนรับบริการใหม่" })}
              </Button>
            </div>
          </FormField>
        </FormGroup>

        <FormGroup style={{ marginBottom: 0 }}>
          <FormField width={10}>
            <label>{"\u00a0"}</label>
          </FormField>
          <FormField width={4}>
            <label>{intl.formatMessage({ id: "REGISTER_SERVICE_เลขที่การนัดหมาย" })}</label>
          </FormField>
          <FormField width={2}>
            <label>{"\u00a0"}</label>
          </FormField>
        </FormGroup>

        <FormGroup style={{ alignItems: "end" }}>
          <FormField width={10}>
            <label>
              {intl.formatMessage({ id: "REGISTER_SERVICE_รายการนัดหมายของผู้รับบริการ" })}
            </label>
          </FormField>
          <FormField width={4}>
            <div style={{ width: "100%" }}>
              <Input
                fluid
                icon="search"
                placeholder={intl.formatMessage({ id: "REGISTER_SERVICE_ค้นหาเลขที่การนัดหมาย" })}
                value={searchText}
                onChange={handleChangeSearch}
              />
            </div>
          </FormField>
          <FormField width={2}>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                setProp={props.setProp}
                paramKey={BTN_ACTS.GET_APPOINTMENT_LIST}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.GET_APPOINTMENT_LIST]}
                //
                fluid
                style={{
                  backgroundColor: "var(--primary-theme-color)",
                  color: "white",
                }}
                onClick={handleSearch}
                title={intl.formatMessage({ id: "REGISTER_SERVICE_ค้นหาข้อมูล" })}
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>

      <Table
        className="mebilling-register-list"
        data={formattedRegisterList}
        headers={`${intl.formatMessage({ id: "REGISTER_SERVICE_เลขที่การนัดหมาย" })},${intl.formatMessage({ id: "REGISTER_SERVICE_วันที่เวลานัดหมาย" })},${intl.formatMessage({ id: "REGISTER_SERVICE_ชื่อผู้ให้บริการ" })},${intl.formatMessage({ id: "REGISTER_SERVICE_รายละเอียดการนัดหมาย" })},${intl.formatMessage({ id: "REGISTER_SERVICE_Package / บริการ" })},`}
        keys="id, estimated_at, doctor_full_name, remark,package_detail, register"
        minRows={DEFAULT_LIST_LIMIT}
        showPagination={false}
        // style={{ height: "calc(50vh - 2rem)" }}
        widths="130, 125, ^135, ^140,^230,^120"
      />

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <PaginationShorthand
          activePage={props.appointmentList?.activePage || 1}
          limit={DEFAULT_LIST_LIMIT}
          total={props.appointmentList?.total || 0}
          onPageChange={handlePageChange}
        />
      </div>

      <ModPackageOrderDetail
        open={!!props.packageDetail?.length}
        onClose={() => {
          props.setProp("MeBillingSequence.packageDetail", null);
        }}
        data={props.packageDetail}
        packageTypeOptions={props.masterOptions?.packageType}
      />

      <ModRegisterServiceFromAppointment
        onEvent={props.onEvent}
        setProp={props.setProp}
        open={!!modRegisterPackage}
        data={modRegisterPackage}
        // options
        doctorList={props.doctorList}
        buttonLoadCheck={props.buttonLoadCheck}
        searchedItemListWithKey={props.searchedItemListWithKey}
        onClose={() => {
          setModRegisterPackage(null);
        }}
        onRegister={(providerName: string) => {
          if (modRegisterPackage) {
            handleRegister(modRegisterPackage, { isConfirm: true, providerName });
          }
        }}
      />
    </div>
  );
};

CardRegisterService.displayName = "CardRegisterService";

export default memo(CardRegisterService);
